import { create } from 'zustand'

import {
  DEFAULT_DISCOVERY_FILTERS,
  normalizeFilters,
  type DiscoveryFilters,
} from './filters'

export const FILTER_STORAGE_PREFIX = 'kuniri.discovery.filters.v2'

interface DiscoveryFilterState {
  userId: string | null
  hydrated: boolean
  filters: DiscoveryFilters
  hydrate: (userId: string) => void
  apply: (filters: DiscoveryFilters) => void
  reset: () => void
}

function storageKey(userId: string): string {
  return `${FILTER_STORAGE_PREFIX}.${userId}`
}

function readStoredFilters(userId: string): DiscoveryFilters {
  if (typeof window === 'undefined') return { ...DEFAULT_DISCOVERY_FILTERS }
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return { ...DEFAULT_DISCOVERY_FILTERS }
    return normalizeFilters(JSON.parse(raw))
  } catch {
    return { ...DEFAULT_DISCOVERY_FILTERS }
  }
}

function writeStoredFilters(userId: string | null, filters: DiscoveryFilters) {
  if (!userId || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(filters))
  } catch {
    // Storage can be denied in private mode; the in-memory filters still work.
  }
}

/**
 * Local filter state only (D-042): no server response is stored here.
 * Preferences are namespaced per user so two accounts on the same browser do
 * not leak a selected city or friend filter into each other.
 */
export const useDiscoveryFilterStore = create<DiscoveryFilterState>()(
  (set, get) => ({
    userId: null,
    hydrated: false,
    filters: { ...DEFAULT_DISCOVERY_FILTERS },

    hydrate: (userId) => {
      if (!userId) return
      if (get().userId === userId && get().hydrated) return
      set({
        userId,
        hydrated: true,
        filters: readStoredFilters(userId),
      })
    },

    apply: (filters) => {
      const normalized = normalizeFilters(filters)
      const userId = get().userId
      writeStoredFilters(userId, normalized)
      set({ filters: normalized })
    },

    reset: () => {
      const defaults = { ...DEFAULT_DISCOVERY_FILTERS }
      writeStoredFilters(get().userId, defaults)
      set({ filters: defaults })
    },
  }),
)
